import React, { useState } from 'react';
import { Client, ClientStatus, useClients } from '../../context/ClientContext';
import { Avatar, Badge, Icon, IconButton, PulseDot, Table } from '../ui';
import type { TableColumn } from '../ui';
import { getAttention, isRecent, relativeDate, STATUS_TONE } from './clientsData';
import { TagChips, TaskMeter } from './ClientBits';
import useMediaQuery, { PHONE_QUERY } from './useMediaQuery';
import { supabase } from '../../lib/supabase';
import { formatForDateInput } from '../../utils/dateUtils';

interface ClientTableProps {
  clients: Client[];
  onSelect: (client: Client) => void;
}

// Table's generic needs an indexable row type; Client is an interface.
type ClientRow = { [K in keyof Client]: Client[K] };

/**
 * Clients table view. Row click opens the preview panel; the Last
 * Activity cell keeps the legacy click-to-edit behavior (writes
 * last_activity_date back to Supabase).
 */
const PHONE_HIDDEN = new Set(['tags', 'accountOwner', 'id']);

export default function ClientTable({ clients, onSelect }: ClientTableProps) {
  const { fetchClients } = useClients();
  const phone = useMediaQuery(PHONE_QUERY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const startEdit = (clientId: string, currentDate: string | null | undefined) => {
    setEditingId(clientId);
    setEditingValue(currentDate || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const saveLastActivity = async (clientId: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      // Handle date properly to avoid timezone conversion issues
      let dateValue: string | null = null;
      if (editingValue) {
        // When editingValue is in YYYY-MM-DD format from the date input,
        // create the date at noon UTC to avoid timezone shifting
        const dateParts = editingValue.split('-');
        if (dateParts.length === 3) {
          const year = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
          const day = parseInt(dateParts[2]);
          dateValue = new Date(Date.UTC(year, month, day, 12, 0, 0)).toISOString();
        } else {
          dateValue = new Date(editingValue).toISOString();
        }
      }

      const { error } = await supabase
        .from('clients')
        .update({ last_activity_date: dateValue })
        .eq('id', clientId);

      if (error) throw error;

      await fetchClients();
      setEditingId(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error updating last activity date:', error);
      alert('Failed to update date. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const columns: TableColumn<ClientRow>[] = [
    {
      key: 'name',
      label: 'Client',
      render: (value, row) => {
        const att = getAttention(row);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '11px' }}>
            <Avatar name={value as string} size={28} />
            <span style={{ minWidth: 0 }}>
              <span
                className="ck-name"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                }}
              >
                {value as string}
                {att ? <PulseDot tone={att.tone} title={att.label} /> : null}
              </span>
              {row.email || row.industry ? (
                <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                  {row.email || row.industry}
                </span>
              ) : null}
            </span>
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => <Badge tone={STATUS_TONE[value as ClientStatus]} dot>{value as string}</Badge>,
    },
    {
      key: 'tags',
      label: 'Tags',
      render: (value) => <TagChips tags={value as string[]} />,
    },
    {
      key: 'tasksDueToday',
      label: 'Tasks Today',
      render: (_value, row) => <TaskMeter client={row} />,
    },
    {
      key: 'lastActivityDate',
      label: 'Last Activity',
      align: 'right',
      mono: true,
      render: (value, row) => {
        if (editingId === row.id) {
          return (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="date"
                className="ck-date-input"
                value={formatForDateInput(editingValue)}
                onChange={(e) => setEditingValue(e.target.value)}
                style={{ width: '150px', height: 'var(--control-sm)' }}
                autoFocus
                disabled={isUpdating}
              />
              <IconButton
                icon="check"
                size="sm"
                variant="outline"
                label="Save"
                disabled={isUpdating}
                onClick={() => saveLastActivity(row.id)}
              />
              <IconButton
                icon="x"
                size="sm"
                label="Cancel"
                disabled={isUpdating}
                onClick={cancelEdit}
              />
            </span>
          );
        }
        const iso = value as string | null | undefined;
        return (
          <span
            className="hover-wash"
            title="Click to edit last activity date"
            onClick={(e) => {
              e.stopPropagation();
              startEdit(row.id, iso);
            }}
            style={{
              display: 'inline-block',
              padding: '2px 6px',
              margin: '-2px -6px',
              borderRadius: 'var(--radius-xs)',
              cursor: 'text',
              color: isRecent(iso) ? 'var(--text-body)' : 'var(--text-faint)',
            }}
          >
            {relativeDate(iso)}
          </span>
        );
      },
    },
    {
      key: 'accountOwner',
      label: 'Owner',
      align: 'center',
      render: (value) => {
        const owner = value as Client['accountOwner'];
        return owner ? (
          <Avatar name={owner.name} size={22} style={{ verticalAlign: 'middle' }} />
        ) : (
          <span style={{ color: 'var(--text-faint)' }}>—</span>
        );
      },
    },
    {
      key: 'id',
      label: '',
      align: 'right',
      render: () => (
        <span className="row-chevron">
          <Icon name="chevron-right" size={15} />
        </span>
      ),
    },
  ];

  // Phones keep the columns worth scanning down; tags and owner are one tap away in the preview panel, and the
  // whole row is the tap target so the chevron goes too. What's left still scrolls sideways inside its own box.
  const shown = phone ? columns.filter((c) => !PHONE_HIDDEN.has(c.key)) : columns;

  return (
    <div className="ck-table-scroll" style={{ overflowX: 'auto' }}>
      <Table<ClientRow>
        columns={shown}
        rows={clients}
        onRowClick={(row) => onSelect(row)}
        style={{ minWidth: phone ? '580px' : '860px' }}
      />
    </div>
  );
}
